<template>
  <VModal v-if="modelValue" v-model="modalVisible" @update:modelValue="closeModal" :click-to-close="!isLoading" :esc-to-close="!isLoading" @opened="handleModalOpened" @closed="handleModalClosed" size="full" :id="'document-modal'" :z-index="9999" :dynamic-width="true">
    <!-- Modal başlık ikonu -->
    <template #icon>
      <span class="material-symbols-outlined text-primary text-xl sm:text-2xl">description</span>
    </template>

    <!-- Modal başlığı -->
    <template #header-content>
      <h2 class="text-base sm:text-xl font-bold text-gray-800 truncate max-w-[150px] sm:max-w-none sm:whitespace-normal">Belge Yönetimi</h2>
    </template>

    <!-- Tab butonları (sadece tablet/desktop görünümlü) -->
    <template #header-actions>
      <div class="hidden md:flex bg-gray-100 p-1 rounded-lg shadow-sm overflow-x-auto max-w-none">
        <button v-for="tab in tabs" :key="tab.value" @click="setActiveTab(tab.value)" class="flex items-center whitespace-nowrap px-3 py-2 rounded-md transition-all duration-200 text-sm font-medium relative" :class="[
            activeTab === tab.value 
              ? 'bg-white text-primary shadow-sm z-10' 
              : 'text-gray-600 hover:text-gray-800'
          ]">
          <span class="material-symbols-outlined mr-2 transition-all duration-200" :class="{
              'text-primary': activeTab === tab.value,
              'text-gray-500': activeTab !== tab.value
            }">
            {{ tab.icon }}
          </span> <span>{{ tab.label }}</span>
        </button>
      </div>
    </template>

    <div class="h-full flex flex-col overflow-hidden relative">
      <!-- Sekme Çubuğu (Sadece Mobil Görünümlü) -->
      <div class="md:hidden bg-white border-b border-gray-200 px-4 py-2">
        <div class="flex bg-gray-100 p-1 rounded-lg shadow-sm w-full">
          <button v-for="tab in tabs" :key="tab.value" @click="setActiveTab(tab.value)" class="flex-1 flex items-center justify-center py-2 px-2 rounded-md transition-all duration-200 text-xs font-medium" :class="[
              activeTab === tab.value 
                ? 'bg-white text-primary shadow-sm' 
                : 'text-gray-600 hover:text-gray-800'
            ]">
            <span class="material-symbols-outlined mr-1" :class="{
                'text-primary': activeTab === tab.value,
                'text-gray-500': activeTab !== tab.value
              }">
              {{ tab.icon }}
            </span> <span>{{ tab.label }}</span>
          </button>
        </div>
      </div>

      <!-- Ana Yükleme Overlay'ı -->
      <div v-if="isLoadingDocuments && !isFilterLoading" class="absolute inset-0 bg-white/80 z-50 flex flex-col items-center justify-center">
        <div class="animate-spin rounded-full h-16 w-16 border-4 border-primary border-t-transparent mb-6"></div>
        <p class="text-primary text-lg font-medium">Belgeler yükleniyor...</p>
      </div>

      <div class="flex flex-col md:flex-row h-full">
        <!-- Sol Panel - Sadece Belgeler sekmesinde göster (Desktop Only) -->
        <div v-if="activeTab === 'documents'" class="hidden md:block w-full md:w-72 bg-gray-50 border-r border-gray-200 p-4 overflow-y-auto">
          <div class="mb-6">
            <h3 class="text-xs uppercase font-semibold text-gray-500 tracking-wider mb-3">Filtreler</h3>

            <!-- Dosya Türü Filtresi -->
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-1">Dosya Türü</label>
              <CustomSelect v-model="filters.fileType" :options="fileTypeOptions" placeholder="Tümü" class="w-full" :clearable="true"/>
            </div>

            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-1">Kategori</label>
              <CategoryPicker v-model="filters.categoryIds" :categories="categoryOptionsFormatted" placeholder="Kategorileri seçin..."/>
            </div>

            <!-- Tarih Filtresi -->
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-1">Tarih</label>
              <CustomSelect v-model="filters.dateFilter" :options="dateFilterOptions" placeholder="Tümü" class="w-full" :clearable="true"/>
            </div>

            <!-- Arama -->
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-1">Arama</label>
              <VInput
                v-model="searchQuery"
                type="text"
                placeholder="Dosya adı veya açıklama ara..."
                :showIcon="true"
                icon="search"
                iconClass="text-gray-400 !text-base"
                :showClearButton="!!searchQuery"
                :showError="false"
                additionalInputClass="w-full text-sm"
              />
            </div>

            <!-- Filtre Aksiyon Butonları -->
            <div class="flex flex-col space-y-2 mt-6">
              <CustomButton button-text="Filtreleri Uygula" @click="() => handleApplyFilters(null)" :is-loading="isFilterLoading" size="sm" :full-width="true"/>
              <button @click="resetFilters" class="text-xs text-primary hover:underline text-center" :disabled="isFilterLoading">
                Filtreleri Sıfırla
              </button>
            </div>
          </div>

          <!-- İstatistikler -->
          <div class="pt-6 border-t border-gray-200">
            <h3 class="text-xs uppercase font-semibold text-gray-500 tracking-wider mb-3">İstatistikler</h3>
            <div class="space-y-3">
              <div class="bg-white rounded-lg p-3 shadow-sm">
                <div class="text-sm font-medium text-gray-700">Toplam Belge</div>
                <div class="text-xl font-bold text-primary">{{ pagination.total || 0 }}</div>
              </div>

              <div class="bg-white rounded-lg p-3 shadow-sm">
                <div class="text-sm font-medium text-gray-700">Aktif Filtreler</div>
                <div class="text-xl font-bold text-primary">{{ activeFilterCount || 0 }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Ana İçerik -->
        <main class="w-full md:flex-1 overflow-auto flex flex-col relative">
          <!-- Belgeleri Listele - Belgeler Sekmesi -->
          <div v-if="activeTab === 'documents'" class="h-full">
            <!-- Belgeler Listesi (Mobilde Sadece Filtre Modalı) -->
            <MobileFilterModal v-model:show="showMobileFilters" :filters="filters" :search-query="searchQuery" :category-options="categoryOptions" :file-type-options="fileTypeOptions" :date-filter-options="dateFilterOptions" :is-loading="isFilterLoading" @apply-filters="handleMobileFilters" @reset-filters="resetFilters" @update:search-query="updateSearchQuery"/>

            <!-- Belgeler Listesi Ana Kısmı -->
            <div class="flex-grow flex flex-col gap-1 overflow-hidden h-full relative px-1">
              <!-- Belge Listesi Bileşeni -->
              <DocumentsListComponent 
                :documents="paginatedDocuments" 
                :isLoading="isFilterLoading && !isLoadingDocuments" 
                :isDeleting="isDeleting" 
                :pagination="pagination" 
                :filters="filters" 
                :searchQuery="searchQuery" 
                :showFilters="showFilters" 
                :categoryOptions="categoryOptions" 
                :fileTypeOptions="fileTypeOptions" 
                :dateFilterOptions="dateFilterOptions" 
                :activeFilters="activeFilters" 
                :hasActiveFilters="hasActiveFilters" 
                :activeFilterCount="activeFilterCount" 
                :isInitialLoading="isLoadingDocuments && documents.length === 0" 
                :selectedDocumentIds="selectedDocumentIds" 
                @refresh="refreshDocuments" 
                @delete="handleDeleteDocument" 
                @bulk-delete="handleBulkDeleteDocuments" 
                @edit-categories="editDocumentCategories" 
                @apply-filters="handleApplyFilters" 
                @reset-filters="resetFilters" 
                @remove-filter="removeFilter" 
                @search="searchDocuments" 
                @clear-search="clearSearch" 
                @update-pagination="updatePagination" 
                @toggle-filters="handleToggleFilters" 
                @update:search-query="updateSearchQuery" 
                @select-document="handleDocumentSelection"
                @sort-change="handleSortChange"
              />
            </div>
          </div>

          <!-- Dosya Yükleme Sekmesi -->
          <div v-else-if="activeTab === 'uploadFile'" class="flex flex-col p-4 overflow-hidden h-full">
            <div class="mb-4">
              <h3 class="text-lg font-semibold text-gray-800 mb-2">Dosya Yükleme</h3>
              <p class="text-gray-600 text-sm">Bu alandan sisteme yeni belgeler yükleyebilirsiniz. Dosyalarınız için kategori ve dosya türü seçmeyi unutmayın.</p>
            </div>

            <!-- FileUploadComponent -->
            <div class="flex-grow overflow-auto">
              <FileUploadComponent :files="files" :categoryOptions="categoryOptions" :fileTypeOptions="fileTypeOptions" :expandAll="expandAll" :expandedFiles="expandedFiles" :hasEmptyFields="hasEmptyFields" @remove-file="removeFile" @update-description="updateDescription" @toggle-expand="toggleExpand" @toggle-expand-all="toggleExpandAll" @process-files="processUploadFiles" @update-categories="updateFileCategories"/>
            </div>

            <!-- Alt Butonlar ve Özet Bilgisi -->
            <div class="mt-2 pt-3 border-t flex justify-between items-center sticky bottom-0 bg-white">
              <div class="text-sm text-gray-600">
                {{ uploadSummary }}
              </div>
              <CustomButton button-text="Dosyaları Yükle" @click="handleFileUpload" :is-loading="isLoading" :is-disabled="isUploadDisabled" size="md" variant="primary"/>
            </div>
          </div>
        </main>
      </div>
    </div>
  </VModal>

  <!-- Delete Confirm Dialog -->
  <DeleteConfirmDialog v-model:show="showDeleteConfirm" :ids="documentToDelete ? [documentToDelete] : selectedDocumentIds" :is-processing="isDeleting" :require-confirmation="selectedDocumentIds && selectedDocumentIds.length > 1" :show-details="selectedDocumentIds && selectedDocumentIds.length > 1" :item-names="documentNames" :error-message="deleteErrorMessage" @confirm="confirmDeleteDocument"/>

  <!-- Kategori Düzenleme Modalı -->
  <VModal v-if="showCategoryEditModal" v-model="showCategoryEditModal" :click-to-close="true" :esc-to-close="true" size="md" :z-index="10000" modalTitle="Kategorileri Düzenle" icon="category" :dynamic-width="true">
    <div class="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <div class="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r">
        <div class="flex items-start">
          <div class="flex-shrink-0">
            <span class="material-symbols-outlined text-blue-500">info</span>
          </div>
          <div class="ml-3">
            <p class="text-sm text-blue-700 font-medium">{{ selectedDocument?.fileName }}</p>
            <p class="mt-1 text-sm text-blue-600">Bu belge için atanacak kategorileri seçin.</p>
          </div>
        </div>
      </div>

      <!-- Kategori Seçimi -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">Kategoriler</label>
        <CategoryPicker v-model="selectedCategories" :categories="categoryOptionsFormatted" placeholder="Kategorileri seçin..."/>
      </div>

      <!-- İşlem Butonları -->
      <div class="flex justify-end space-x-3 pt-4 border-t">
        <CustomButton button-text="İptal" @click="showCategoryEditModal = false" variant="secondary" size="md"/>
        <CustomButton button-text="Kaydet" @click="saveDocumentCategories" :is-loading="isSavingCategories" size="md"/>
      </div>
    </div>
  </VModal>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import VModal from '@/components/modals/VModal.vue'
import CustomButton from '@/components/base/buttons/VButton.vue'
import CustomSelect from '@/components/base/inputs/VSelect.vue'
import VInput from '@/components/base/inputs/VInput.vue'

// Import utility functions for category handling
import { normalizeCategoryId } from './utils/category-utils'

// Özel bileşenler
import MobileFilterModal from './components/MobileFilterModal.vue'
import DocumentsListComponent from './components/DocumentsListComponent.vue'
import CategoryPicker from './components/CategoryPicker.vue'
import FileUploadComponent from './components/FileUploadComponent.vue'
import DeleteConfirmDialog from './components/DeleteConfirmDialog.vue'

// Composable
import { useAssetsInformation } from './composables'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:modelValue', 'update:show'])

// Assets Information Composable'dan gerekli state ve metodları al
const {
  // State
  documents,
  isLoadingDocuments,
  categoryOptions,
  files,
  isLoading,
  isDeleting,
  showDeleteConfirm,
  documentToDelete,
  filters,
  searchQuery,
  showFilters,
  pagination,
  expandedFiles,
  expandAll,
  isFilterLoading,
  selectedDocumentIds,
  deleteErrorMessage,
  sortState,

  // Hesaplanan değerler
  fileTypeOptions,
  dateFilterOptions,
  activeFilters,
  hasActiveFilters,
  activeFilterCount,
  paginatedDocuments,
  hasEmptyFields,
  uploadSummary,
  isUploadDisabled,

  // Metodlar
  loadDocuments,
  loadCategories,
  handleUploadFiles,
  processUploadFiles,
  handleDeleteDocument,
  confirmDeleteDocument,
  cancelDeleteDocument,
  handleBulkDeleteDocuments,
  handleUpdateCategories,
  applyFilters,
  resetFilters,
  removeFilter,
  updatePagination,
  toggleExpand,
  toggleExpandAll,
  removeFile,
  updateDescription,
  searchDocuments,
  clearSearch,
  toggleFilters,
  refreshDocuments,
  updateFileCategories,
  handleDocumentSelection,
  handleSortChange,
} = useAssetsInformation()

// Modal durumları
const modalVisible = ref(false)
const showMobileFilters = ref(false) // Mobil filtre modali
const windowWidth = ref(window.innerWidth)

// Screen size detection
const isMobile = computed(() => windowWidth.value < 768)

// Window resize listener
const updateWindowWidth = () => {
  windowWidth.value = window.innerWidth
}

// ResizeObserver fix by debouncing height changes
let resizeDebounceTimeout = null
const handleResize = () => {
  if (resizeDebounceTimeout) {
    clearTimeout(resizeDebounceTimeout)
  }

  resizeDebounceTimeout = setTimeout(() => {
    updateWindowWidth()
  }, 100)
}

// Add resize event listener
onMounted(() => {
  window.addEventListener('resize', handleResize)
})

// Remove event listeners on unmount
onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  if (resizeDebounceTimeout) {
    clearTimeout(resizeDebounceTimeout)
  }
})

// DeleteConfirmDialog yönetimi
watch(showDeleteConfirm, (newValue) => {
  if (!newValue) {
    deleteErrorMessage.value = ''
  }
})

// Belge isimleri için bir harita oluşturma
const documentNames = computed(() => {
  const names = {}
  documents.value.forEach(doc => {
    names[doc.id] = doc.fileName || `Belge #${doc.id}`
  })
  return names
})

// Sekmeler
const activeTab = ref('documents')
const tabs = [
  { value: 'documents', label: 'Belgeler', icon: 'description' },
  { value: 'uploadFile', label: 'Dosya Yükle', icon: 'upload_file' },
]

// Kategori düzenleme modalı
const showCategoryEditModal = ref(false)
const selectedDocument = ref(null)
const selectedCategories = ref([])
const categorySearchQuery = ref('')
const isSavingCategories = ref(false)

// Kategorileri düzgün formatta döndürme
const categoryOptionsFormatted = computed(() => {
  // Çocukları olan bir ağaç yapısı oluştur
  return buildCategoryTree(categoryOptions.value)
})

// Kategori ağacı oluşturan yardımcı fonksiyon
const buildCategoryTree = (categories) => {
  if (!categories || !Array.isArray(categories)) return []

  // Önce ID'ye göre tüm kategorileri haritala
  const categoryMap = {}
  categories.forEach(cat => {
    categoryMap[cat.value] = {
      id: cat.value,
      label: cat.label,
      parentId: cat.parentId || null,
      children: [],
    }
  })

  // Şimdi ağacı oluştur
  const rootCategories = []
  categories.forEach(cat => {
    if (cat.parentId && categoryMap[cat.parentId]) {
      // Eğer parent'i varsa, parent'in child'larına ekle
      categoryMap[cat.parentId].children.push(categoryMap[cat.value])
    } else {
      // Parent'i yoksa veya geçersizse, kök kategorilere ekle
      rootCategories.push(categoryMap[cat.value])
    }
  })

  return rootCategories
}

// Hiyerarşik kategoriler oluştur
const hierarchicalCategories = computed(() => {
  if (!categoryOptions.value || !Array.isArray(categoryOptions.value)) return []

  // Kategorileri ID'ye göre saklayacak nesne
  const categoryMap = {}

  // Tüm kategorileri önce map'e ekle
  categoryOptions.value.forEach(cat => {
    categoryMap[cat.value] = {
      id: cat.value,
      label: cat.label,
      children: [],
      parentId: cat.parentId || null,
    }
  })

  // Hiyerarşiyi oluştur
  const rootCategories = []

  // Kategorileri yerleştir
  categoryOptions.value.forEach(cat => {
    if (cat.parentId) {
      // Alt kategori ise, ebeveyn kategorisinin alt kategorilerine ekle
      if (categoryMap[cat.parentId]) {
        categoryMap[cat.parentId].children.push(categoryMap[cat.value])
      } else {
        // Ebeveyn bulunamazsa, kök kategorilere ekle
        rootCategories.push(categoryMap[cat.value])
      }
    } else {
      // Ebeveyn ID'si yoksa, kök kategori
      rootCategories.push(categoryMap[cat.value])
    }
  })

  return rootCategories
})


// Aktif sekmeyi değiştir
const setActiveTab = (tabValue) => {
  if (showDeleteConfirm.value) {
    cancelDeleteDocument()
  }
  activeTab.value = tabValue
}

// Dosya yükleme tamamlandığında belge sekmesine dön
const goToDocumentsTab = () => {
  setActiveTab('documents')
}

// Modal açıldığında
const handleModalOpened = async () => {
  try {
    // Önce kategorileri, sonra belgeleri yükle
    await loadCategories()
    await loadDocuments()

    // Fix ResizeObserver loop error by using nextTick and setTimeout
    await nextTick()
    setTimeout(() => {
      updateWindowWidth()
    }, 50)
  } catch (error) {
    console.error('Modal açılırken hata oluştu:', error)
  }
}

// Modal kapandığında
const handleModalClosed = () => {
  resetState()
}

// Modal kapatma
const closeModal = () => {
  emit('update:modelValue', false)
  emit('update:show', false)
}

// Durumu sıfırla
const resetState = () => {
  files.value = []
  activeTab.value = 'documents'
  cancelDeleteDocument()
  showCategoryEditModal.value = false
  showMobileFilters.value = false
}

// Belge kategorilerini düzenleme
const editDocumentCategories = (document) => {
  selectedDocument.value = document
  // Şu anda seçili kategorileri ayarla
  selectedCategories.value = document.categories ? document.categories.map(cat => cat.id) : []
  showCategoryEditModal.value = true
}

// Belge kategorilerini kaydetme
const saveDocumentCategories = async () => {
  if (!selectedDocument.value) return

  isSavingCategories.value = true

  try {
    // Ensure category IDs are properly normalized 
    const normalizedCategoryIds = selectedCategories.value.map(id => normalizeCategoryId(id))

    // IMPORTANT: Make sure we're using the document's own assetId, not the global one
    const documentAssetId = selectedDocument.value.assetId

    await handleUpdateCategories(
        normalizeCategoryId(selectedDocument.value.id),
        normalizedCategoryIds,
        documentAssetId, // Pass the document's specific assetId
    )
    showCategoryEditModal.value = false
  } catch (error) {
    console.error('Kategorileri kaydederken hata oluştu:', error)
  } finally {
    isSavingCategories.value = false
  }
}

// Handler for toggle filters button with mobile support
const handleToggleFilters = () => {
  if (isMobile.value) {
    // Show mobile filter modal on mobile screens
    showMobileFilters.value = true
  } else {
    // Toggle sidebar on desktop
    toggleFilters()
  }
}

// Filtreleri uygula
const handleApplyFilters = (localFilters) => {
  if (localFilters) {
    filters.fileType = localFilters.fileType
    filters.categoryIds = localFilters.categoryIds || []
    filters.dateFilter = localFilters.dateFilter
  }
  applyFilters()
}

// Mobil filtreleri uygula
const handleMobileFilters = (mobileFilters) => {
  if (mobileFilters) {
    filters.fileType = mobileFilters.fileType
    filters.categoryIds = mobileFilters.categoryIds || []
    filters.dateFilter = mobileFilters.dateFilter
    if (mobileFilters.searchQuery !== undefined) {
      searchQuery.value = mobileFilters.searchQuery
    }
  }
  applyFilters()
}

// Dosya yükleme işlemini yönet ve başarılı olduğunda belge sekmesine geç
const handleFileUpload = async () => {
  try {
    await handleUploadFiles()
    // Yükleme başarılı olduysa belge sekmesine dön
    goToDocumentsTab()
  } catch (error) {
    console.error('Dosya yükleme hatası:', error)
  }
}

// Arama alanını güncelleme
const updateSearchQuery = (value) => {
  searchQuery.value = value
}

// Props izleme
watch(() => props.modelValue, (newValue) => {
  modalVisible.value = newValue
})

// Modal görünürlüğü değişikliklerini izle
watch(modalVisible, (newValue) => {
  if (!newValue) {
    emit('update:modelValue', false)
  }
})
</script>

<style scoped>
/* Hide scrollbar for Chrome, Safari and Opera */
.thin-scrollbar::-webkit-scrollbar {
  width: 4px;
  height: 4px;
}

.thin-scrollbar::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.thin-scrollbar::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 10px;
}

.thin-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #555;
}

/* Fix for ResizeObserver error */
:deep(.modal-dialog) {
  height: auto !important;
  max-height: 95vh !important;
}

:deep(.modal-content) {
  height: auto !important;
  max-height: 95vh !important;
  overflow: hidden;
}
</style>